<script setup lang="ts">
import { useAuth0 } from '@auth0/auth0-vue'
import { useUserStore } from '../stores/user'
import { useBranding } from '../composables/useBranding'
import { clearDevToken, getDevToken, useAuth } from '../composables/useAuth'
import { ref, onMounted, onBeforeUnmount, computed, watch, provide, nextTick, type Component } from 'vue'
import { useCmShortcuts } from '../composables/useCmShortcuts'
import { createAccount, getAccounts } from '../services/api'
import { getTodayData } from '../services/advisorTodayService'
import { createStakeholder, createActivity, tagActivityToDeal, triggerExtraction, getWorkspaceDeals } from '../services/advisorWorkspaceApi'
import type { AdvisorActivityType, WorkspaceDeal } from '../types/workspace'
import { supplierNavItems, advisorNavItems, adminNavItems, cmNavItems, type NavItem } from '../config/nav'
import { useRouter, useRoute } from 'vue-router'
import { useTour } from '../onboarding/useTour'
import NotificationBell from '../components/NotificationBell.vue'
// TermsModal mounted globally in App.vue so it renders on standalone
// views (onboarding) too — not imported here.
import CmNotificationPanel from '../components/cm/CmNotificationPanel.vue'
import {
    LayoutDashboard, Building2, Package, Target, MessageSquare, Users,
    BookOpen, TrendingUp, Map, UserCog, Settings, FileText, LifeBuoy,
    Bot, GraduationCap, Calendar, CheckSquare, User, Landmark, DollarSign,
    Brain, Lightbulb, Microscope, Tag, Handshake, Ticket,
    Kanban, Sparkles, Megaphone, Search, Building, ContactRound,
    LogOut, ChevronDown, Plus, Zap, MessageCircle, Activity, UserPlus,
    Video, FolderOpen, BarChart3, SlidersHorizontal, Plug, Wifi, Radar, Compass, Lock
} from 'lucide-vue-next'

const iconMap: Record<string, Component> = {
    LayoutDashboard, Building2, Package, Target, MessageSquare, Users,
    BookOpen, TrendingUp, Map, UserCog, Settings, FileText, LifeBuoy,
    Bot, GraduationCap, Calendar, CheckSquare, User, Landmark, DollarSign,
    Brain, Lightbulb, Microscope, Tag, Handshake, Ticket,
    Kanban, Sparkles, Megaphone, Search, Building, ContactRound,
    Zap, MessageCircle, Activity, UserPlus, Video, FolderOpen, BarChart3,
    SlidersHorizontal, Plug, Wifi, Radar
}

const auth = useAuth0()
const { loginWithRedirect, user } = auth
const userStore = useUserStore()
const isAuthenticated = computed(() => auth.isAuthenticated.value || userStore.isAuthenticated)
// Header avatar: advisor's uploaded logo, else the Auth0 profile picture
const avatarUrl = computed(() => userStore.user?.advisor?.logoUrl || (user.value as { picture?: string } | undefined)?.picture || null)
// Where the logo click lands: the signed-in user's own dashboard ("/" would
// bounce through the landing/login page).
const homeRoute = computed(() => userStore.user ? userStore.getDefaultRoute() : '/')
const router = useRouter()
const { brandLogoUrl } = useBranding()
const isDark = ref(localStorage.getItem('theme') === 'dark' || document.documentElement.classList.contains('dark'))

const route = useRoute()
const searchQuery = ref('')
const searchInputRef = ref<HTMLInputElement | null>(null)
const isMobileMenuOpen = ref(false)

// Console breadcrumb derived from the route (CC / SECTION / PAGE)
const breadcrumbs = computed(() => {
  // Routes can name their crumbs explicitly (meta.breadcrumb); otherwise derive from the URL
  const explicit = route.meta.breadcrumb as string[] | undefined
  if (explicit) return ['CC', ...explicit]
  const segs = route.path.split('/').filter(Boolean)
    .filter(s => !/^[0-9a-f-]{8,}$/i.test(s) && !/^\d+$/.test(s))
  // Portal root (/supplier, /admin): append the page name so it reads CC / SUPPLIER / DASHBOARD
  if (segs.length === 1 && segs[0] === currentPersona.value) {
    const label = navItems.value.find(i => i.to === route.path)?.label
    if (label) segs.push(label)
  }
  return ['CC', ...segs.slice(-2).map(s => s.replace(/-/g, ' '))]
})

// Collapsible sidebar — sidebarCollapsed is the only source of truth.
// Width, content offset, labels, logo, toggle icon/aria all derive from it
// (see the .cc-shell.is-collapsed CSS vars at the bottom of this file).
const readCollapsed = () => {
  try { return localStorage.getItem('sidebarCollapsed') === 'true' } catch { return false }
}
const sidebarCollapsed = ref(readCollapsed())

// Tooltip: one teleported element, positioned from the hovered item's rect.
// Native title tooltips are OS-delayed (~1s) and clipped by scroll boxes.
// 'right' for the collapsed sidebar rail, 'bottom' for the top bar, 'left' for right-edge menus.
// The trigger gets aria-describedby while the tip shows; Esc dismisses it.
type TipPlace = 'right' | 'bottom' | 'left'
const tip = ref<{ text: string; top: number; left: number; place: TipPlace; arrow?: number } | null>(null)
const tipEl = ref<HTMLElement | null>(null)
let tipTarget: HTMLElement | null = null
const showTip = (e: Event, text: string, place: TipPlace = 'right') => {
  if (!text) return // nothing to show (e.g. an expanded nav item with no hint)
  const el = e.currentTarget as HTMLElement
  // A mouse press focuses the button too; only keyboard focus shows the tip, else it flashes on every click.
  if (e.type === 'focusin' && !el.matches(':focus-visible')) return
  const r = el.getBoundingClientRect()
  tip.value = place === 'bottom'
    ? { text, top: r.bottom + 8, left: r.left + r.width / 2, place }
    : place === 'left'
      ? { text, top: r.top + r.height / 2, left: r.left - 10, place }
      : { text, top: r.top + r.height / 2, left: r.right + 10, place }
  tipTarget?.removeAttribute('aria-describedby')
  tipTarget = el
  el.setAttribute('aria-describedby', 'cc-tip')
  // A bottom tip near the viewport edge slides back on-screen; the arrow stays on the trigger.
  if (place === 'bottom') nextTick(() => {
    const t = tip.value
    if (!t || !tipEl.value) return
    const half = tipEl.value.offsetWidth / 2
    const x = Math.min(Math.max(t.left, half + 8), window.innerWidth - half - 8)
    t.arrow = t.left - x
    t.left = x
  })
}
const onTipEsc = (e: KeyboardEvent) => { if (e.key === 'Escape' && tip.value) hideTip() }
onMounted(() => window.addEventListener('keydown', onTipEsc))
onBeforeUnmount(() => window.removeEventListener('keydown', onTipEsc))
// Upgrade popover for tier-locked nav items. Same teleport-from-rect approach,
// but interactive: a short close delay lets the pointer cross onto it.
const upsell = ref<{ item: NavItem; top: number; left: number } | null>(null)
let upsellTimer: ReturnType<typeof setTimeout> | undefined
const openUpsell = (e: Event, item: NavItem) => {
  hideTip()
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
  upsell.value = { item, top: r.top + r.height / 2, left: r.right + 18 }
}
const keepUpsell = () => clearTimeout(upsellTimer)
const closeUpsellSoon = () => { clearTimeout(upsellTimer); upsellTimer = setTimeout(() => { upsell.value = null }, 120) }
const onLockedBlur = (e: FocusEvent) => {
  if (!(e.relatedTarget instanceof Node && document.getElementById('cc-upsell')?.contains(e.relatedTarget))) hideTip()
}
const hideTip = () => {
  tip.value = null; clearTimeout(upsellTimer); upsell.value = null
  tipTarget?.removeAttribute('aria-describedby'); tipTarget = null
}
// Pages inside the layout reuse the same tooltip (e.g. the Sweep view-bar toggles).
// Components that render AppLayout themselves (SweepShell) get it as the default slot's ccTip prop.
const ccTip = { showTip, hideTip }
provide('ccTip', ccTip)

const toggleSidebar = () => {
  hideTip()
  sidebarCollapsed.value = !sidebarCollapsed.value
  try { localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed.value)) } catch { /* storage unavailable */ }
}

// Close mobile menu and dropdowns on route change
router.afterEach(() => {
  isMobileMenuOpen.value = false
  hideTip()
  closeDropdowns()
})

// Guided tour: auto-start once per user per role, on the first page under
// the tour's route prefix. Short delay lets the layout settle before the
// overlay measures elements.
// Re-checked after the delay: the user may have been redirected (e.g. to the
// setup wizard) or this layout may be gone, so require the sidebar on screen.
const { shouldAutoStart, start: startTour, restart: restartTour, stop: stopTour } = useTour()
let tourTimer: ReturnType<typeof setTimeout> | undefined
watch(
  () => [userStore.user?.id, route.path] as const,
  ([, path]) => {
    clearTimeout(tourTimer)
    if (!shouldAutoStart(path)) return
    tourTimer = setTimeout(() => {
      if (shouldAutoStart(route.path) && document.querySelector('.cc-sidebar')) startTour('auto')
    }, 500)
  },
  { immediate: true }
)
onBeforeUnmount(() => clearTimeout(tourTimer))

const handleSearch = () => {
    if (!searchQuery.value.trim()) return

    // Determine context based on persona
    if (currentPersona.value === 'advisor') {
        router.push({ path: '/advisor/search', query: { q: searchQuery.value } })
        searchQuery.value = ''
    } else if (currentPersona.value === 'supplier') {
        router.push({ path: '/supplier/products', query: { search: searchQuery.value } })
    }
}

// Dark mode logic
const toggleDark = () => {
  isDark.value = !isDark.value
  if (isDark.value) {
    document.documentElement.classList.add('dark')
    localStorage.setItem('theme', 'dark')
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.setItem('theme', 'light')
  }
}

// Ensure dark class matches state on mount
onMounted(() => {
  if (isDark.value) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
})

// Persona Detection - determined by route and user role
const currentPersona = computed({
  get: () => {
    // Route-based detection takes priority
    if (route.path === '/' || route.path === '/login' || route.path === '/signup') return 'landing'
    if (route.path.startsWith('/cm')) return 'cm'
    if (route.path.startsWith('/supplier')) return 'supplier'
    if (route.path.startsWith('/advisor')) return 'advisor'
    if (route.path.startsWith('/admin')) return 'admin'

    // Fallback to user role
    if (userStore.isChannelManager) return 'cm'
    if (userStore.isSupplier) return 'supplier'
    if (userStore.isAdvisor) return 'advisor'
    if (userStore.isAdmin) return 'admin'

    return 'landing'
  },
  set: (val) => {
    if (val === 'cm') router.push('/cm/today')
    else if (val === 'supplier') router.push('/supplier')
    else if (val === 'advisor') router.push('/advisor/workspace/deals')
    else if (val === 'admin') router.push('/admin')
    else router.push('/')
  }
})

// Header identity — the single place the signed-in user is shown
const identityName = computed(() => user.value?.name || userStore.user?.name || userStore.user?.email || '')
const identityEmail = computed(() => user.value?.email || userStore.user?.email || '')
const identityCompany = computed(() =>
  userStore.user?.supplierEmployee?.supplier?.name
  || userStore.user?.advisor?.firmName
  || (currentPersona.value === 'cm' ? 'Channel Manager' : userStore.isAdmin ? 'Administrator' : userStore.isAdvisor ? 'Advisor' : '')
)
const identitySubtitle = computed(() => [identityCompany.value, identityEmail.value].filter(Boolean).join(' · '))
const identityAriaLabel = computed(() => `Account menu for ${identityName.value}, ${identityCompany.value}`)

// Channel Manager (MDF): the router guard can't see isMdfOnly until the user
// profile loads, so re-check reactively once it does.
watch(
  () => [userStore.isMdfOnly, route.path] as const,
  ([mdfOnly, path]) => {
    if (mdfOnly && path.startsWith('/supplier') && !path.startsWith('/supplier/mdf')) {
      router.replace('/supplier/mdf/requests')
    }
  },
  { immediate: true }
)

// Sidebar Nav
// Items belonging to a gated feature (NavItem.feature) are hidden unless the
// backend granted it on the auth/me response. Same predicate as the API gate
// and the router guard, so the three never disagree.
const hasFeature = (item: NavItem) => {
  if (!item.feature) return true
  const features = (userStore.user as { features?: Record<string, boolean> } | null)?.features
  return !!features?.[item.feature]
}
// Advisors with Sweep get a "Find connectivity" shortcut in Quick Add — the entry point
// cold users looked for there first (intuitiveness testing) before finding the nav item.
const canSweep = computed(() => currentPersona.value === 'advisor' && !!(userStore.user as { features?: Record<string, boolean> } | null)?.features?.sweep)

const navItems = computed(() => {
  if (currentPersona.value === 'cm') return cmNavItems.filter(hasFeature)
  if (currentPersona.value === 'supplier') {
    // Channel Manager (MDF) sees only the MDF pages.
    if (userStore.isMdfOnly) return supplierNavItems.filter(i => i.to.includes('/mdf/')).filter(hasFeature)
    return supplierNavItems.filter(hasFeature)
  }
  if (currentPersona.value === 'advisor') return advisorNavItems.filter(hasFeature)
  if (currentPersona.value === 'admin') return adminNavItems.filter(hasFeature)
  return []
})

// Group Nav Items by Section
const navSections = computed(() => {
    const sections: Record<string, typeof navItems.value> = {}
    navItems.value.forEach(item => {
        const key = item.section || 'Main'
        if (!sections[key]) sections[key] = []
        sections[key].push(item)
    })
    return sections
})

// CM keyboard shortcuts (only active when persona is 'cm')
useCmShortcuts(
  router,
  () => { searchInputRef.value?.focus() },
  () => currentPersona.value === 'cm'
)

// Tier gating helpers
const tierRank: Record<string, number> = { FREE: 0, BASIC: 1, ENTERPRISE: 2 }

function isItemLocked(item: NavItem): boolean {
    if (!item.requiredTier) return false
    if (userStore.isImpersonating) return false
    if (currentPersona.value !== 'supplier') return false
    const current = userStore.supplierTier ?? 'FREE'
    return (tierRank[current] ?? 0) < (tierRank[item.requiredTier] ?? 0)
}

const showUpgradeModal = ref(false)
const lockedFeatureName = ref('')

function handleLockedClick(item: NavItem) {
    hideTip()
    lockedFeatureName.value = item.label
    showUpgradeModal.value = true
}

// Header dropdown state (advisor only)
const supplierMenuOpen = ref(sessionStorage.getItem('nav:supplierMenuOpen') === 'true')
const accountsMenuOpen = ref(sessionStorage.getItem('nav:accountsMenuOpen') === 'true')
watch(supplierMenuOpen, (v) => sessionStorage.setItem('nav:supplierMenuOpen', String(v)))
watch(accountsMenuOpen, (v) => sessionStorage.setItem('nav:accountsMenuOpen', String(v)))

const settingsDropdownOpen = ref(false)
const userDropdownOpen = ref(false)

// Plan block: "PROFESSIONAL" → "Professional"
const planLabel = computed(() => {
    const raw = userStore.isAdvisor ? userStore.advisorTier : userStore.isSupplier ? userStore.supplierTier : userStore.isAdmin ? 'Admin' : ''
    const t = String(raw || '')
    return t ? t.charAt(0) + t.slice(1).toLowerCase() : ''
})

// Live promo count for the Promotions sub-item (advisor only; reuses the Today endpoint)
const promoCount = ref(0)
onMounted(async () => {
    if (currentPersona.value !== 'advisor' || !userStore.user) return
    try {
        const data = await getTodayData(getAccessTokenSilently, ['active_promos'])
        promoCount.value = data.active_promos?.length ?? 0
    } catch { /* badge is decorative; stay silent */ }
})

const advisorSettingsTabs = [
    { label: 'Profile', tab: 'profile' },
    { label: 'Defaults', tab: 'defaults' },
    { label: 'Today Page', tab: 'today-page' },
    { label: 'TSD Networks', tab: 'tsd-networks' },
    { label: 'Suppliers', tab: 'suppliers' },
    { label: 'CRM Integrations', tab: 'crm-integrations' },
    { label: 'Webhooks', tab: 'webhooks' },
    { label: 'Notifications', tab: 'notifications' },
    { label: 'Billing', tab: 'billing' },
]

// Settings cog contents per persona; the cog hides when empty.
// Supplier has none: its settings live in the sidebar.
const settingsMenuItems = computed(() => {
    if (currentPersona.value === 'advisor') return advisorSettingsTabs.map(i => ({ label: i.label, to: `/advisor/settings?tab=${i.tab}` }))
    if (currentPersona.value === 'cm') return [{ label: 'Settings', to: '/cm/settings' }]
    return []
})

// Close dropdowns on outside click
const closeDropdowns = () => {
    settingsDropdownOpen.value = false
    userDropdownOpen.value = false
    showPlusDropdown.value = false
}

const handleLogout = () => {
  const wasDevLogin = !!getDevToken()
  stopTour()
  userStore.clearUser()
  clearDevToken()

  if (wasDevLogin && !auth.isAuthenticated.value) {
    // Dev-login user: no Auth0 session to destroy, just redirect locally
    router.push('/login')
  } else {
    // Auth0 SDK user: use Auth0's logout endpoint to clear the session
    auth.logout({ logoutParams: { returnTo: window.location.origin } })
  }
}

const handleLogin = () => {
  loginWithRedirect()
}

// Global Quick Add Account
const { getAccessTokenSilently } = useAuth()
const showGlobalQuickAdd = ref(false)
const quickAddName = ref('')
const quickAddContact = ref('')
const quickAddReferral = ref('')
const quickAddNote = ref('')
const savingGlobalLead = ref(false)
const showLeadToast = ref(false)
const lastLeadAccountId = ref<string | null>(null)

// Plus button dropdown
const showPlusDropdown = ref(false)

// Intake modal state
const showIntake = ref(false)
const intakeType = ref<AdvisorActivityType>('MEETING')
const intakeTitle = ref('')
const intakeContent = ref('')
const intakeAccountId = ref('')
const intakeNewAccountName = ref('')
const intakeDealId = ref('')
const intakeParticipants = ref('')
const intakeRunAI = ref(true)
const intakeAccounts = ref<{ id: string; name: string }[]>([])
const intakeAccountDeals = ref<WorkspaceDeal[]>([])
const savingIntake = ref(false)
const intakeError = ref('')

const openIntakeModal = async () => {
  showPlusDropdown.value = false
  showIntake.value = true
  intakeType.value = 'MEETING'
  intakeTitle.value = ''
  intakeContent.value = ''
  intakeAccountId.value = ''
  intakeNewAccountName.value = ''
  intakeDealId.value = ''
  intakeParticipants.value = ''
  intakeRunAI.value = true
  intakeError.value = ''
  try {
    const token = await getAccessTokenSilently()
    intakeAccounts.value = await getAccounts(token)
  } catch { /* best effort */ }
}

const loadAccountDeals = async (accountId: string) => {
  intakeAccountDeals.value = []
  intakeDealId.value = ''
  if (!accountId || accountId === '__new__') return
  try {
    const token = await getAccessTokenSilently()
    const allDeals = await getWorkspaceDeals(token)
    intakeAccountDeals.value = allDeals.filter((d: WorkspaceDeal) => d.account_id === accountId)
  } catch { /* best effort */ }
}

watch(() => intakeAccountId.value, (val) => loadAccountDeals(val))

const submitIntake = async () => {
  if (!intakeTitle.value || !intakeContent.value) return
  if (!intakeAccountId.value && !intakeNewAccountName.value) return
  savingIntake.value = true
  intakeError.value = ''
  try {
    const token = await getAccessTokenSilently()

    // 1. Create account if needed
    let accountId = intakeAccountId.value
    if (accountId === '__new__') {
      const account = await createAccount({ name: intakeNewAccountName.value }, token)
      accountId = account.id
    }

    // 2. Create the activity
    const isTranscript = intakeType.value === 'MEETING' || intakeType.value === 'CALL'
    const activity = await createActivity(accountId, {
      type: intakeType.value,
      date: new Date().toISOString(),
      title: intakeTitle.value,
      description: intakeContent.value,
      has_transcript: isTranscript,
      transcript_content: isTranscript ? intakeContent.value : undefined,
      participants: intakeParticipants.value ? intakeParticipants.value.split(',').map(s => s.trim()).filter(Boolean) : undefined,
    }, token)

    // 3. Tag to deal if selected
    if (intakeDealId.value && activity.id) {
      try { await tagActivityToDeal(activity.id, intakeDealId.value, token) } catch { /* best effort */ }
    }

    // 4. Trigger AI extraction if checked
    if (intakeRunAI.value && activity.id) {
      try { await triggerExtraction(activity.id, token) } catch { /* best effort */ }
    }

    // 5. Navigate to the activity detail
    showIntake.value = false
    router.push(`/advisor/workspace/accounts/${accountId}/activities/${activity.id}`)
  } catch (e: unknown) {
    intakeError.value = e instanceof Error ? e.message : 'Failed to save'
  } finally {
    savingIntake.value = false
  }
}

const saveGlobalLead = async () => {
  if (!quickAddName.value.trim()) return
  savingGlobalLead.value = true
  try {
    const token = await getAccessTokenSilently()
    const account = await createAccount({ name: quickAddName.value }, token)

    if (quickAddContact.value.trim() && account.id) {
      try { await createStakeholder(account.id, { name: quickAddContact.value }, token) } catch { /* best effort */ }
    }

    const noteParts: string[] = []
    if (quickAddReferral.value.trim()) noteParts.push(`Referral: ${quickAddReferral.value}`)
    if (quickAddNote.value.trim()) noteParts.push(quickAddNote.value)

    if (noteParts.length > 0 && account.id) {
      try {
        await createActivity(account.id, {
          type: 'NOTE',
          date: new Date().toISOString(),
          title: 'Lead captured',
          description: noteParts.join('\n'),
        }, token)
      } catch { /* best effort */ }
    }

    lastLeadAccountId.value = account.id
    showLeadToast.value = true
    setTimeout(() => { showLeadToast.value = false }, 8000)

    showGlobalQuickAdd.value = false
    quickAddName.value = ''
    quickAddContact.value = ''
    quickAddReferral.value = ''
    quickAddNote.value = ''
  } catch (e) { console.error(e) }
  finally { savingGlobalLead.value = false }
}
</script>

<template>
  <div class="cc-shell relative h-full w-full bg-cc-deep transition-colors duration-300 overflow-hidden print:h-auto print:overflow-visible" :class="{ 'is-collapsed': sidebarCollapsed }" @click="closeDropdowns">

    <!-- Mobile Backdrop -->
    <div
      v-if="isMobileMenuOpen"
      class="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm transition-opacity"
      @click="isMobileMenuOpen = false"
    ></div>

    <!-- Left Sidebar -->
    <aside
      class="cc-sidebar print:hidden bg-cc-surface border-r border-cc-border cc-bezel flex flex-col absolute inset-y-0 z-50 overflow-hidden md:translate-x-0"
      :class="isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'"
    >

      <!-- Logo Area -->
      <div class="relative h-16 flex-shrink-0 border-b border-cc-border">
        <!-- Logo goes HOME (role dashboard), not to the landing/login page —
             advisors expect it to act like "back to where I started".
             Both logo variants stay mounted and crossfade, so neither is ever
             squeezed into a width it doesn't fit. -->
        <router-link :to="homeRoute" class="absolute inset-0 transition-opacity opacity-80 hover:opacity-100">
          <img
            class="cc-logo cc-logo-full h-8 w-auto max-w-[150px] object-contain"
            :src="brandLogoUrl || (isDark ? '/assets/companion-on-dark.svg' : '/assets/companion-on-light.svg')"
            :alt="brandLogoUrl ? 'Workspace logo' : 'The Channel Standard'"
          />
          <img
            class="cc-logo cc-logo-mark h-6 w-auto max-w-10 object-contain"
            :src="brandLogoUrl || '/favicon.svg'"
            :alt="brandLogoUrl ? 'Workspace logo' : 'The Channel Standard'"
          />
        </router-link>
      </div>

      <!-- Navigation Links -->
      <nav class="cc-nav flex-1 min-h-0 overflow-y-auto overflow-x-hidden" @scroll.passive="hideTip">

        <div v-for="(items, section) in navSections" :key="section" :class="section !== 'Main' ? 'cc-nav-section' : ''">
            <!-- Fixed-height group header: label when expanded, divider when collapsed -->
            <div v-if="section !== 'Main'" class="cc-section-head relative mb-2">
                <div class="cc-rail-only absolute inset-x-0 top-1/2 border-t border-cc-border"></div>
                <h3 class="cc-nav-label px-4 font-mono text-[10px] font-medium text-cc-text-tertiary uppercase tracking-[0.12em]">
                    {{ section }}
                </h3>
            </div>
            <div class="space-y-1">
                <template v-for="item in items" :key="item.to">
                    <!-- Coming Soon items: greyed out, not clickable -->
                    <div
                        v-if="item.comingSoon"
                        class="cc-nav-item flex items-center justify-between pr-4 py-2 text-sm font-medium rounded-lg text-cc-text-tertiary cursor-default select-none"
                        @mouseenter="sidebarCollapsed && showTip($event, item.label + ' (Coming Soon)')" @mouseleave="hideTip" @focusin="sidebarCollapsed && showTip($event, item.label + ' (Coming Soon)')" @focusout="hideTip"
                    >
                        <span class="flex items-center">
                            <component v-if="item.icon && iconMap[item.icon]" :is="iconMap[item.icon]" :size="18" class="mr-3 shrink-0" />
                            <span class="cc-nav-label">{{ item.label }}</span>
                        </span>
                        <span class="cc-nav-label font-mono text-[9px] font-semibold uppercase tracking-[0.06em] text-cc-text-secondary bg-cc-track-soft px-1.5 py-0.5 rounded">Soon</span>
                    </div>
                    <!-- Tier-locked items: muted row + "Pro" chip; hover/focus opens the upgrade popover -->
                    <button
                        v-else-if="isItemLocked(item)"
                        type="button"
                        :data-tour="item.tour"
                        :aria-label="`${item.label}, paid plan feature`"
                        :aria-describedby="upsell?.item === item ? 'cc-upsell' : undefined"
                        @click="handleLockedClick(item)"
                        class="cc-nav-item cc-locked flex items-center justify-between gap-2 w-full pr-4 py-2 text-sm font-medium rounded-lg hover:bg-cc-hover transition-colors cursor-pointer"
                        @mouseenter="openUpsell($event, item)" @mouseleave="closeUpsellSoon" @focusin="openUpsell($event, item)" @focusout="onLockedBlur" @keydown.esc="hideTip"
                    >
                        <span class="flex items-center flex-1 min-w-0">
                            <component v-if="item.icon && iconMap[item.icon]" :is="iconMap[item.icon]" :size="18" class="mr-3 shrink-0" />
                            <span class="cc-nav-label flex-1 min-w-0 overflow-hidden text-ellipsis text-left" :title="item.label">{{ item.label }}</span>
                        </span>
                        <span class="cc-nav-label cc-pro-chip">
                            <Lock :size="11" :stroke-width="2.25" aria-hidden="true" />
                            <span class="cc-pro-text">Pro</span>
                        </span>
                    </button>
                    <!-- Accounts with collapsible sub-items (advisor only) -->
                    <div v-else-if="item.label === 'Accounts' && currentPersona === 'advisor'">
                        <div
                            :data-tour="item.tour"
                            class="cc-nav-item w-full flex items-center justify-between pr-1 text-sm font-medium rounded-lg transition-colors group"
                            :class="[
                              $route.path === item.to || $route.path.startsWith('/advisor/workspace/accounts') || $route.path.startsWith('/advisor/deal-intelligence') || $route.path.startsWith('/advisor/assets')
                                ? 'bg-cc-hover text-cc-text'
                                : 'text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text'
                            ]"
                        >
                            <button
                                @click="$router.push(item.to)"
                                class="flex-1 flex items-center text-left py-2"
                                @mouseenter="sidebarCollapsed && showTip($event, item.label)" @mouseleave="hideTip" @focusin="sidebarCollapsed && showTip($event, item.label)" @focusout="hideTip"
                            >
                                <component v-if="item.icon && iconMap[item.icon]" :is="iconMap[item.icon]" :size="18" class="mr-3 shrink-0 group-hover:scale-110 transition-transform" />
                                <span class="cc-nav-label">{{ item.label }}</span>
                            </button>
                            <button
                                v-if="!sidebarCollapsed"
                                @click.stop="accountsMenuOpen = !accountsMenuOpen"
                                class="p-2 -mr-1 hover:bg-cc-hover rounded transition-colors"
                                :title="accountsMenuOpen ? 'Collapse' : 'Expand'"
                            >
                                <svg :class="['w-3 h-3 transition-transform duration-200', accountsMenuOpen ? 'rotate-90' : '']" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                                </svg>
                            </button>
                        </div>
                        <div v-if="accountsMenuOpen && !sidebarCollapsed" class="cc-nav-sub ml-[1.65rem] mt-1 pl-2 space-y-0.5 border-l border-cc-border-med">
                            <router-link
                                to="/advisor/deal-intelligence"
                                class="block px-3 py-1.5 text-[12px] rounded transition-colors"
                                :class="$route.path.startsWith('/advisor/deal-intelligence') ? 'text-cc-text font-medium bg-cc-hover' : 'text-cc-text-secondary hover:text-cc-text hover:bg-cc-hover'"
                            >
                                Deal Intelligence
                            </router-link>
                            <span
                                class="block px-3 py-1.5 text-[12px] rounded text-cc-text-tertiary cursor-default flex items-center gap-1.5"
                            >
                                Assets
                                <span class="text-[10px] uppercase tracking-wider text-cc-text-tertiary bg-cc-hover px-1.5 py-0.5 rounded">Soon</span>
                            </span>
                        </div>
                    </div>
                    <!-- Supplier Directory with collapsible sub-items (advisor only) -->
                    <div v-else-if="item.label === 'Supplier Directory' && currentPersona === 'advisor'">
                        <div
                            class="cc-nav-item w-full flex items-center justify-between pr-1 text-sm font-medium rounded-lg transition-colors group"
                            :class="[
                              $route.path === item.to || $route.path.startsWith('/advisor/suppliers') || $route.path.startsWith('/advisor/products') || $route.path.startsWith('/advisor/product-explorer') || $route.path.startsWith('/advisor/promotions') || $route.path.startsWith('/advisor/training')
                                ? 'bg-cc-hover text-cc-text'
                                : 'text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text'
                            ]"
                        >
                            <button
                                @click="$router.push(item.to)"
                                class="flex-1 flex items-center text-left py-2"
                                @mouseenter="sidebarCollapsed && showTip($event, item.label)" @mouseleave="hideTip" @focusin="sidebarCollapsed && showTip($event, item.label)" @focusout="hideTip"
                            >
                                <component v-if="item.icon && iconMap[item.icon]" :is="iconMap[item.icon]" :size="18" class="mr-3 shrink-0 group-hover:scale-110 transition-transform" />
                                <span class="cc-nav-label">{{ item.label }}</span>
                            </button>
                            <button
                                v-if="!sidebarCollapsed"
                                @click.stop="supplierMenuOpen = !supplierMenuOpen"
                                class="p-2 -mr-1 hover:bg-cc-hover rounded transition-colors"
                                :title="supplierMenuOpen ? 'Collapse' : 'Expand'"
                            >
                                <svg :class="['w-3 h-3 transition-transform duration-200', supplierMenuOpen ? 'rotate-90' : '']" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                                </svg>
                            </button>
                        </div>
                        <div v-if="supplierMenuOpen && !sidebarCollapsed" class="cc-nav-sub ml-[1.65rem] mt-1 pl-2 space-y-0.5 border-l border-cc-border-med">
                            <router-link
                                to="/advisor/products"
                                class="block px-3 py-1.5 text-[12px] rounded transition-colors"
                                :class="$route.path.startsWith('/advisor/products') ? 'text-cc-text font-medium bg-cc-hover' : 'text-cc-text-secondary hover:text-cc-text hover:bg-cc-hover'"
                            >
                                Product Directory
                            </router-link>
                            <router-link
                                to="/advisor/promotions"
                                class="flex items-center justify-between px-3 py-1.5 text-[12px] rounded transition-colors"
                                :class="$route.path.startsWith('/advisor/promotions') ? 'text-cc-text font-medium bg-cc-hover' : 'text-cc-text-secondary hover:text-cc-text hover:bg-cc-hover'"
                            >
                                Promotions
                                <span v-if="promoCount" class="font-mono text-[10px] font-semibold leading-none px-1.5 py-0.5 rounded bg-cc-primary-subtle text-cc-primary">{{ promoCount }}</span>
                            </router-link>
                            <router-link
                                to="/advisor/product-explorer"
                                class="block px-3 py-1.5 text-[12px] rounded transition-colors"
                                :class="$route.path.startsWith('/advisor/product-explorer') ? 'text-cc-text font-medium bg-cc-hover' : 'text-cc-text-secondary hover:text-cc-text hover:bg-cc-hover'"
                            >
                                Product Explorer
                            </router-link>
                            <router-link
                                to="/advisor/training"
                                class="block px-3 py-1.5 text-[12px] rounded transition-colors"
                                :class="$route.path.startsWith('/advisor/training') ? 'text-cc-text font-medium bg-cc-hover' : 'text-cc-text-secondary hover:text-cc-text hover:bg-cc-hover'"
                            >
                                Training Center
                            </router-link>
                        </div>
                    </div>
                    <!-- Standard nav item -->
                    <router-link
                        v-else
                        :to="item.to"
                        :data-tour="item.tour"
                        class="cc-nav-item relative flex items-center pr-4 py-2 text-sm font-medium rounded-lg transition-colors group"
                        :class="[
                          $route.path === item.to
                            ? 'bg-cc-elevated text-cc-text font-semibold ring-1 ring-cc-border-med'
                            : 'text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text'
                        ]"
                        :aria-current="$route.path === item.to ? 'page' : undefined"
                        @mouseenter="showTip($event, sidebarCollapsed ? item.label : (item.hint || ''))" @mouseleave="hideTip" @focusin="showTip($event, sidebarCollapsed ? item.label : (item.hint || ''))" @focusout="hideTip"
                    >
                        <component v-if="item.icon && iconMap[item.icon]" :is="iconMap[item.icon]" :size="18" class="mr-3 shrink-0 group-hover:scale-110 transition-transform" aria-hidden="true" />
                        <span class="cc-nav-label">{{ item.label }}</span>
                    </router-link>
                </template>
            </div>
        </div>

      </nav>

      <!-- Tier Info — fixed height in both states so the toggle never moves.
           Expanded: PLAN text. Collapsed: compact tier badge. They crossfade. -->
      <div v-if="userStore.user" class="relative h-[78px] flex-shrink-0 border-t border-cc-border bg-cc-surface overflow-hidden">
         <div class="cc-nav-label absolute inset-0 p-3 whitespace-nowrap">
           <div class="h-full rounded-lg border border-cc-border-med bg-cc-elevated px-3 flex flex-col justify-center min-w-0">
             <div class="font-mono text-[10px] uppercase tracking-[0.06em] text-cc-text-tertiary">Plan</div>
             <div class="text-sm font-semibold truncate" :class="userStore.advisorTier === 'PROFESSIONAL' || userStore.supplierTier === 'ENTERPRISE' ? 'text-cc-primary' : 'text-cc-text-secondary'">{{ planLabel }}</div>
           </div>
         </div>
         <div class="cc-rail-only absolute inset-0 flex items-center justify-center">
           <div class="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold" :class="userStore.advisorTier === 'PROFESSIONAL' || userStore.supplierTier === 'ENTERPRISE' ? 'bg-cc-success/10 text-cc-success border border-cc-success/20' : 'bg-cc-hover text-cc-text-secondary border border-cc-border-med'"
             @mouseenter="showTip($event, 'Plan: ' + (userStore.isAdvisor ? (userStore.advisorTier ?? 'Free') : userStore.isSupplier ? (userStore.supplierTier ?? 'Free') : 'Admin'))" @mouseleave="hideTip"
           >
             {{ userStore.isAdvisor ? (userStore.advisorTier || '').charAt(0) : userStore.isSupplier ? (userStore.supplierTier || '').charAt(0) : 'A' }}
           </div>
         </div>
      </div>

      <!-- Collapse Toggle — pinned last so its screen position never depends on the tier block -->
      <button
        type="button"
        @click="toggleSidebar"
        class="hidden md:flex flex-shrink-0 items-center justify-center border-t border-cc-border py-3 text-cc-text-tertiary hover:text-cc-text hover:bg-cc-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cc-primary"
        @mouseenter="showTip($event, sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar')" @mouseleave="hideTip" @focusin="showTip($event, sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar')" @focusout="hideTip"
        :aria-label="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        :aria-expanded="!sidebarCollapsed"
      >
        <svg class="w-4 h-4 transition-transform duration-300" :class="sidebarCollapsed ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
        </svg>
      </button>

    </aside>

    <!-- Main Content Area — explicit width (no flex-1) so it can't collapse -->
    <div class="cc-main flex flex-col w-full min-w-0 h-full overflow-hidden print:h-auto print:overflow-visible">

      <!-- Top Header -->
      <header class="print:hidden relative bg-cc-surface border-b border-cc-border cc-bezel h-16 flex-shrink-0 z-40 flex items-center justify-between px-4 md:px-8">

         <!-- Mobile Menu Button -->
         <button
           @click="isMobileMenuOpen = !isMobileMenuOpen"
           class="md:hidden text-cc-text-secondary hover:text-cc-text p-2 -ml-2 mr-2"
         >
           <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
         </button>

         <!-- Breadcrumb (Console) -->
         <nav v-if="isAuthenticated" class="hidden lg:flex items-center gap-1.5 mr-5 shrink-0 font-mono text-[11px] tracking-[0.12em] uppercase">
           <template v-for="(c, i) in breadcrumbs" :key="i">
             <span :class="i === 0 ? 'text-cc-primary font-semibold' : i === breadcrumbs.length - 1 ? 'text-cc-text font-semibold' : 'text-cc-text-secondary'">{{ c }}</span>
             <span v-if="i < breadcrumbs.length - 1" class="text-cc-text-tertiary">/</span>
           </template>
         </nav>

         <!-- Search -->
         <div class="flex-1 min-w-0 max-w-[600px] mr-4">
            <div class="relative" data-tour="search">
              <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-cc-text-secondary">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
              </span>
              <input
                ref="searchInputRef"
                v-model="searchQuery"
                @keyup.enter="handleSearch"
                type="text"
                aria-label="Search"
                class="block w-full h-10 pl-10 pr-14 border border-cc-border-med rounded-lg leading-5 bg-cc-elevated text-cc-text placeholder-cc-text-tertiary shadow-[0_1px_2px_rgba(20,30,25,0.04)] focus:outline-none focus:ring-2 focus:ring-cc-primary focus:border-transparent sm:text-sm transition-colors"
                placeholder="Search accounts, suppliers, products…"
              />
              <kbd class="absolute inset-y-0 right-2 my-auto h-5 hidden md:flex items-center px-1.5 rounded font-mono text-[10px] text-cc-text-tertiary border border-cc-border-med bg-cc-surface">⌘K</kbd>
            </div>
         </div>

         <!-- Right Actions -->
         <div class="flex items-center gap-1.5 shrink-0">

            <!-- Quick Add dropdown -->
            <div v-if="isAuthenticated && currentPersona === 'advisor'" class="relative">
              <button
                type="button"
                aria-label="Quick add"
                data-tour="quick-add"
                @click.stop="showPlusDropdown = !showPlusDropdown; hideTip()"
                @mouseenter="showTip($event, 'Quick Add', 'bottom')" @mouseleave="hideTip" @focusin="showTip($event, 'Quick Add', 'bottom')" @focusout="hideTip"
                class="w-9 h-9 rounded-lg bg-cc-primary hover:bg-cc-primary-hover text-cc-btn-text flex items-center justify-center transition-colors"
              >
                <Plus :size="18" aria-hidden="true" />
              </button>
              <div v-if="showPlusDropdown" class="absolute right-0 mt-2 w-56 bg-cc-elevated rounded-lg border border-cc-border shadow-cc-dropdown z-50 py-1 animate-fade-in-up">
                <button v-if="canSweep" @click="showPlusDropdown = false; $router.push('/advisor/sweep')"
                  class="flex items-center gap-3 w-full px-4 py-2 text-sm text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text transition-colors text-left">
                  <Radar :size="16" />
                  Find connectivity <span class="text-[11px] text-cc-text-tertiary">· Sweep</span>
                </button>
                <button @click="showPlusDropdown = false; $router.push('/advisor/workspace/accounts/new')"
                  class="flex items-center gap-3 w-full px-4 py-2 text-sm text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text transition-colors text-left">
                  <Building :size="16" />
                  New Account
                </button>
                <button @click="openIntakeModal()"
                  class="flex items-center gap-3 w-full px-4 py-2 text-sm text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text transition-colors text-left">
                  <FileText :size="16" />
                  Add Meeting / Note
                </button>
              </div>
            </div>

            <!-- Dark Mode Slider -->
            <button
              type="button"
              class="relative inline-flex h-6 w-11 mx-1.5 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-cc-primary focus-visible:ring-offset-2 focus-visible:ring-offset-cc-surface"
              :class="isDark ? 'bg-cc-primary' : 'bg-cc-track-soft'"
              role="switch"
              :aria-checked="isDark"
              aria-label="Dark mode"
              @mouseenter="showTip($event, isDark ? 'Switch to light mode' : 'Switch to dark mode', 'bottom')" @mouseleave="hideTip" @focusin="showTip($event, isDark ? 'Switch to light mode' : 'Switch to dark mode', 'bottom')" @focusout="hideTip"
              @click="toggleDark(); hideTip()"
            >
              <span
                aria-hidden="true"
                class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                :class="isDark ? 'translate-x-5' : 'translate-x-0'"
              >
                <span class="absolute inset-0 flex items-center justify-center" v-if="!isDark"><svg class="w-3 h-3 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clip-rule="evenodd"/></svg></span>
                <span class="absolute inset-0 flex items-center justify-center" v-else><svg class="w-3 h-3 text-blue-300" fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/></svg></span>
              </span>
            </button>

            <!-- Notifications -->
            <span v-if="isAuthenticated" class="inline-flex" @mouseenter="showTip($event, 'Notifications', 'bottom')" @mouseleave="hideTip" @focusin="showTip($event, 'Notifications', 'bottom')" @focusout="hideTip" @click="hideTip">
              <CmNotificationPanel v-if="currentPersona === 'cm'" />
              <NotificationBell v-else />
            </span>

            <!-- Auth / Profile — cog + user dropdowns, every persona -->
            <template v-if="isAuthenticated">
              <div class="flex items-center gap-1.5">

                <!-- Settings Cog Dropdown -->
                <div v-if="settingsMenuItems.length" class="relative">
                  <button
                    type="button"
                    aria-label="Settings"
                    @click.stop="settingsDropdownOpen = !settingsDropdownOpen; userDropdownOpen = false; hideTip()"
                    class="w-9 h-9 flex items-center justify-center rounded-lg text-cc-text-secondary hover:text-cc-text hover:bg-cc-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cc-primary"
                    :class="{ 'bg-cc-hover text-cc-text': settingsDropdownOpen }"
                    :aria-expanded="settingsDropdownOpen"
                    @mouseenter="showTip($event, 'Settings', 'bottom')" @mouseleave="hideTip" @focusin="showTip($event, 'Settings', 'bottom')" @focusout="hideTip"
                  >
                    <Settings :size="18" aria-hidden="true" />
                  </button>
                  <div
                    v-if="settingsDropdownOpen"
                    class="absolute right-0 mt-2 w-48 bg-cc-elevated rounded-lg border border-cc-border shadow-cc-dropdown z-50 py-1 animate-fade-in-up"
                  >
                    <router-link
                      v-for="item in settingsMenuItems"
                      :key="item.to"
                      :to="item.to"
                      class="block px-4 py-2 text-sm text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text transition-colors"
                      @click="settingsDropdownOpen = false"
                    >
                      {{ item.label }}
                    </router-link>
                  </div>
                </div>

                <!-- User Avatar Dropdown -->
                <div class="relative pl-3 ml-1.5 border-l border-cc-border-med h-8 flex items-center">
                  <button
                    data-tour="account-menu"
                    @click.stop="userDropdownOpen = !userDropdownOpen; settingsDropdownOpen = false; hideTip()"
                    class="flex items-center gap-2 p-1 rounded-lg hover:bg-cc-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cc-primary"
                    :class="{ 'bg-cc-hover': userDropdownOpen }"
                    :aria-expanded="userDropdownOpen"
                    @mouseenter="showTip($event, identityEmail, 'bottom')" @mouseleave="hideTip" @focusin="showTip($event, identityEmail, 'bottom')" @focusout="hideTip"
                    :aria-label="identityAriaLabel"
                  >
                    <div class="text-right hidden xl:block min-w-0 max-w-[11rem]">
                      <div class="text-sm font-medium text-cc-text truncate" :title="identityName">{{ identityName }}</div>
                      <div class="text-xs text-cc-text-secondary truncate">{{ identitySubtitle }}</div>
                    </div>
                    <img v-if="avatarUrl" :src="avatarUrl" class="h-8 w-8 rounded-full border border-cc-border-med object-cover bg-white" />
                    <div v-else class="h-8 w-8 rounded-full bg-cc-primary-subtle text-cc-primary flex items-center justify-center font-bold text-sm border border-cc-border">
                      {{ (user?.name || userStore.user?.name || '?').charAt(0) }}
                    </div>
                    <ChevronDown :size="14" class="text-cc-text-tertiary hidden sm:block" />
                  </button>
                  <div
                    v-if="userDropdownOpen"
                    class="absolute right-0 top-full mt-6 w-56 bg-cc-elevated rounded-lg border border-cc-border shadow-cc-dropdown z-50 py-1 animate-fade-in-up"
                  >
                    <div class="px-4 py-2.5 border-b border-cc-border">
                      <div class="text-sm font-medium text-cc-text truncate">{{ user?.name || userStore.user?.name }}</div>
                      <div class="text-xs text-cc-text-tertiary truncate">{{ user?.email || userStore.user?.email }}</div>
                    </div>
                    <router-link
                      to="/resources/docs"
                      class="flex items-center gap-3 px-4 py-2 text-sm text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text transition-colors"
                      @click="userDropdownOpen = false"
                    >
                      <FileText :size="16" />
                      Documentation
                    </router-link>
                    <router-link
                      to="/resources/support"
                      class="flex items-center gap-3 px-4 py-2 text-sm text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text transition-colors"
                      @click="userDropdownOpen = false"
                    >
                      <LifeBuoy :size="16" />
                      Help & Support
                    </router-link>
                    <button
                      type="button"
                      class="flex items-center gap-3 w-full px-4 py-2 text-sm text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text transition-colors text-left"
                      @click="userDropdownOpen = false; restartTour()"
                    >
                      <Compass :size="16" />
                      Take the tour
                    </button>
                    <div class="border-t border-cc-border my-1"></div>
                    <button
                      @click="handleLogout"
                      class="flex items-center gap-3 w-full px-4 py-2 text-sm text-cc-danger hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <LogOut :size="16" />
                      Logout
                    </button>
                  </div>
                </div>

              </div>
            </template>

            <button v-else @click="handleLogin" class="bg-cc-primary hover:bg-cc-primary-hover text-cc-btn-text px-4 py-2 rounded-cc-sm text-sm font-medium transition">
              Log In
            </button>

         </div>
      </header>

      <!-- Page Content -->
      <main class="flex-1 p-4 md:p-8 md:pb-4 overflow-y-auto overflow-x-hidden print:overflow-visible print:p-0">
        <slot :cc-tip="ccTip" />
      </main>

      <!-- Footer -->
      <footer class="print:hidden px-6 py-2 text-center text-xs text-cc-text-tertiary">
        &copy; {{ new Date().getFullYear() }} Channel Companion LLC. All rights reserved. Channel Companion&trade; is a trademark of Channel Companion LLC.
      </footer>

    </div>

    <!-- Global Quick Add Modal -->
    <div v-if="showGlobalQuickAdd" class="fixed inset-0 z-50 flex items-center justify-center" style="background: rgba(0,0,0,0.4)">
      <div @click.stop style="background: var(--bg-elevated); border-radius: 8px; box-shadow: var(--shadow-dropdown); padding: 24px; width: 100%; max-width: 420px;">
        <h2 style="font-size: 17px; font-weight: 600; color: var(--text-primary); margin-bottom: 16px">Quick Add Account</h2>
        <div style="display: flex; flex-direction: column; gap: 10px;">
          <input v-model="quickAddName" placeholder="Company name *"
            style="width: 100%; padding: 9px 14px; background: var(--bg-surface); border-radius: 6px; border: 1px solid var(--border-med); font-size: 13px; color: var(--text-primary); outline: none;" />
          <input v-model="quickAddContact" placeholder="Contact name"
            style="width: 100%; padding: 9px 14px; background: var(--bg-surface); border-radius: 6px; border: 1px solid var(--border-med); font-size: 13px; color: var(--text-primary); outline: none;" />
          <input v-model="quickAddReferral" placeholder="Referral source"
            style="width: 100%; padding: 9px 14px; background: var(--bg-surface); border-radius: 6px; border: 1px solid var(--border-med); font-size: 13px; color: var(--text-primary); outline: none;" />
          <textarea v-model="quickAddNote" placeholder="Quick note..." rows="2"
            style="width: 100%; padding: 9px 14px; background: var(--bg-surface); border-radius: 6px; border: 1px solid var(--border-med); font-size: 13px; color: var(--text-primary); outline: none; resize: none;" />
        </div>
        <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px;">
          <button @click="showGlobalQuickAdd = false" style="padding: 9px 18px; font-size: 11px; font-weight: 500; color: var(--text-secondary); border-radius: 6px;">Cancel</button>
          <button @click="saveGlobalLead" :disabled="!quickAddName.trim() || savingGlobalLead"
            style="padding: 9px 18px; font-size: 11px; font-weight: 600; background: var(--primary); color: white; border-radius: 6px; cursor: pointer;"
            :style="{ opacity: !quickAddName.trim() || savingGlobalLead ? 0.5 : 1 }">
            {{ savingGlobalLead ? 'Saving...' : 'Save Lead' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Lead created toast -->
    <div v-if="showLeadToast"
      class="fixed bottom-6 right-6 z-50 flex items-center gap-3"
      style="background: var(--bg-elevated); border-radius: 8px; box-shadow: var(--shadow-dropdown); padding: 14px 20px;">
      <span style="font-size: 13px; font-weight: 500; color: var(--success)">Lead created</span>
      <router-link v-if="lastLeadAccountId" :to="`/advisor/workspace/accounts/${lastLeadAccountId}`"
        style="font-size: 11px; font-weight: 600; color: var(--primary)">
        View →
      </router-link>
      <button @click="showLeadToast = false" style="color: var(--text-tertiary); font-size: 11px; margin-left: 8px">✕</button>
    </div>

    <!-- Upgrade Prompt Modal -->
    <Teleport to="body">
      <div v-if="showUpgradeModal" class="fixed inset-0 z-[100] flex items-center justify-center">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showUpgradeModal = false"></div>
        <div class="relative bg-cc-elevated rounded-xl shadow-cc-dropdown max-w-sm w-full mx-4 p-6 text-center">
          <div class="mx-auto w-12 h-12 rounded-full bg-cc-primary-subtle flex items-center justify-center mb-4">
            <svg class="w-6 h-6 text-cc-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 class="text-lg font-bold text-cc-text mb-2">Enterprise Feature</h3>
          <p class="text-sm text-cc-text-secondary mb-6">
            <span class="font-medium text-cc-text">{{ lockedFeatureName }}</span>
            requires an Enterprise plan. Upgrade to unlock full access to all features.
          </p>
          <div class="flex gap-3">
            <button
              @click="showUpgradeModal = false"
              class="flex-1 px-4 py-2 border border-cc-border-med text-cc-text-secondary rounded-cc-sm text-sm font-medium hover:bg-cc-hover transition"
            >
              Maybe Later
            </button>
            <router-link
              to="/supplier/settings"
              @click="showUpgradeModal = false"
              class="flex-1 px-4 py-2 bg-cc-primary text-cc-btn-text rounded-cc-sm text-sm font-medium hover:bg-cc-primary-hover transition text-center"
            >
              View Plans
            </router-link>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Intake Modal -->
    <Teleport to="body">
      <div v-if="showIntake" class="fixed inset-0 z-[100] flex items-center justify-center">
        <div class="absolute inset-0 bg-black/50" @click="showIntake = false"></div>
        <div class="relative bg-cc-elevated rounded-cc w-[640px] max-h-[80vh] overflow-y-auto" style="box-shadow: var(--shadow-dropdown)" @click.stop>
          <div class="p-5 border-b border-cc-border/20">
            <h2 class="text-lg font-bold text-cc-text">Add Intelligence</h2>
            <p class="text-sm text-cc-text-secondary mt-1">Paste a meeting transcript, note, or email. We'll figure out where it goes.</p>
          </div>

          <div class="p-5 space-y-4">
            <!-- Type selector -->
            <div class="flex gap-2">
              <button v-for="t in (['MEETING', 'NOTE', 'EMAIL', 'CALL'] as AdvisorActivityType[])" :key="t"
                @click="intakeType = t"
                :class="['px-3 py-1.5 rounded-cc-sm text-sm font-medium', intakeType === t ? 'bg-cc-primary text-cc-btn-text' : 'bg-cc-surface text-cc-text-secondary border border-cc-border']">
                {{ t.charAt(0) + t.slice(1).toLowerCase() }}
              </button>
            </div>

            <!-- Title -->
            <input v-model="intakeTitle" type="text" placeholder="Title (e.g., Discovery Call with Dr. Torres)"
              class="w-full px-3 py-2 rounded-cc-sm border border-cc-border bg-cc-surface text-cc-text text-sm focus:outline-none focus:ring-2 focus:ring-cc-primary/20" />

            <!-- Content -->
            <textarea v-model="intakeContent" rows="8"
              :placeholder="intakeType === 'MEETING' || intakeType === 'CALL' ? 'Paste meeting transcript here...' : 'Type your note or paste email content...'"
              class="w-full px-3 py-2 rounded-cc-sm border border-cc-border bg-cc-surface text-cc-text text-sm resize-y focus:outline-none focus:ring-2 focus:ring-cc-primary/20" />

            <!-- Account matching -->
            <div>
              <label class="text-[11px] font-semibold uppercase tracking-wider text-cc-text-tertiary">Account</label>
              <div class="flex gap-2 mt-1">
                <select v-model="intakeAccountId"
                  class="flex-1 px-3 py-2 rounded-cc-sm border border-cc-border bg-cc-surface text-cc-text text-sm focus:outline-none focus:ring-2 focus:ring-cc-primary/20">
                  <option value="">Select or create account...</option>
                  <option value="__new__">+ Create New Account</option>
                  <option v-for="a in intakeAccounts" :key="a.id" :value="a.id">{{ a.name }}</option>
                </select>
                <input v-if="intakeAccountId === '__new__'" v-model="intakeNewAccountName" placeholder="Account name"
                  class="flex-1 px-3 py-2 rounded-cc-sm border border-cc-border bg-cc-surface text-cc-text text-sm focus:outline-none focus:ring-2 focus:ring-cc-primary/20" />
              </div>
            </div>

            <!-- Deal matching -->
            <div v-if="intakeAccountId && intakeAccountId !== '__new__' && intakeAccountDeals.length > 0">
              <label class="text-[11px] font-semibold uppercase tracking-wider text-cc-text-tertiary">Deal</label>
              <select v-model="intakeDealId"
                class="w-full mt-1 px-3 py-2 rounded-cc-sm border border-cc-border bg-cc-surface text-cc-text text-sm focus:outline-none focus:ring-2 focus:ring-cc-primary/20">
                <option value="">Don't link to a deal</option>
                <option v-for="d in intakeAccountDeals" :key="d.id" :value="d.id">{{ d.name }}</option>
              </select>
            </div>

            <!-- Participants (for meetings/calls) -->
            <div v-if="intakeType === 'MEETING' || intakeType === 'CALL'">
              <label class="text-[11px] font-semibold uppercase tracking-wider text-cc-text-tertiary">Participants</label>
              <input v-model="intakeParticipants" type="text" placeholder="Names, comma separated"
                class="w-full mt-1 px-3 py-2 rounded-cc-sm border border-cc-border bg-cc-surface text-cc-text text-sm focus:outline-none focus:ring-2 focus:ring-cc-primary/20" />
            </div>

            <!-- Error message -->
            <p v-if="intakeError" class="text-sm text-cc-danger">{{ intakeError }}</p>
          </div>

          <div class="p-5 border-t border-cc-border/20 flex items-center justify-between">
            <label class="flex items-center gap-2 text-sm text-cc-text-secondary cursor-pointer">
              <input type="checkbox" v-model="intakeRunAI" class="rounded" />
              Run AI extraction
            </label>
            <div class="flex gap-2">
              <button @click="showIntake = false" class="px-4 py-2 text-sm text-cc-text-secondary hover:text-cc-text transition-colors">Cancel</button>
              <button @click="submitIntake" :disabled="!intakeTitle || !intakeContent || (!intakeAccountId && !intakeNewAccountName) || savingIntake"
                class="px-4 py-2 bg-cc-primary text-cc-btn-text rounded-cc-sm text-sm font-medium disabled:opacity-50 hover:bg-cc-primary-hover transition-colors">
                {{ savingIntake ? 'Saving...' : 'Save & Analyze' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
  <Teleport to="body">
    <div
      v-if="tip"
      id="cc-tip"
      ref="tipEl"
      role="tooltip"
      class="cc-tip fixed z-[1000] pointer-events-none w-max max-w-xs rounded-md bg-cc-text text-cc-surface text-xs font-medium px-2.5 py-1.5"
      :class="{ 'is-bottom': tip.place === 'bottom', 'is-left': tip.place === 'left' }"
      :style="{ top: tip.top + 'px', left: tip.left + 'px', '--tip-arrow': (tip.arrow ?? 0) + 'px' }"
    >{{ tip.text }}</div>
    <div
      v-if="upsell"
      id="cc-upsell"
      role="tooltip"
      class="cc-upsell fixed z-[60]"
      :style="{ top: upsell.top + 'px', left: upsell.left + 'px' }"
      @mouseenter="keepUpsell" @mouseleave="closeUpsellSoon" @keydown.esc="hideTip"
    >
      <p class="cc-upsell-title">Available on paid plans</p>
      <p class="cc-upsell-body">Upgrade your account to view and edit {{ upsell.item.label }}.</p>
      <button type="button" class="cc-upsell-btn" @click="handleLockedClick(upsell.item)">Upgrade plan</button>
    </div>
  </Teleport>
</template>

<style scoped>
/* One CSS variable drives sidebar width AND main-content offset, so they can't drift. */
.cc-shell { --sidebar-w: 16rem; --nav-px: 1rem; --nav-item-pl: 1rem; --nav-gap: 1.5rem; --section-head-h: 15px; }
/* Icon pad is a % of the item width so it stays centered even when the nav scrollbar narrows the pill */
.cc-shell.is-collapsed { --sidebar-w: 4rem; --nav-px: 0.5rem; --nav-item-pl: calc(50% - 9px); --nav-gap: 1rem; --section-head-h: 1px; }

.cc-sidebar { width: var(--sidebar-w); transition: width 200ms ease, transform 300ms ease-in-out; }
@media (min-width: 768px) {
  .cc-main { margin-left: var(--sidebar-w); width: calc(100% - var(--sidebar-w)); transition: margin-left 200ms ease, width 200ms ease; }
}

.cc-nav { padding: 1rem var(--nav-px); transition: padding 200ms ease; }
.cc-nav > * + * { margin-top: var(--nav-gap); transition: margin-top 200ms ease; }
/* Group header: 15px label row when expanded, 1px divider when collapsed */
.cc-section-head { height: var(--section-head-h); transition: height 200ms ease; }
.cc-nav-item { padding-left: var(--nav-item-pl); overflow: hidden; white-space: nowrap; transition: padding-left 200ms ease, background-color 150ms ease, color 150ms ease; }
/* Divider above non-Main groups (Settings, Resources). Collapsed rail already draws one in the section head. */
.cc-nav-section { border-top: 1px solid var(--color-cc-border); padding-top: 0.75rem; }
.is-collapsed .cc-nav-section { border-top: 0; padding-top: 0; }

/* Labels stay mounted. Collapse fades them out at once; expand fades them in near the end of the width change. */
.cc-nav-label { white-space: nowrap; transition: opacity 150ms ease 80ms; }
.is-collapsed .cc-nav-label { opacity: 0; transition-delay: 0ms; }
.cc-rail-only { opacity: 0; transition: opacity 150ms ease; }
.is-collapsed .cc-rail-only { opacity: 1; }

.cc-logo { position: absolute; top: 50%; translate: 0 -50%; transition: opacity 150ms ease; }
.cc-logo-full { left: 1.5rem; }
.cc-logo-mark { left: 2rem; translate: -50% -50%; opacity: 0; } /* centered in the 4rem rail */
.is-collapsed .cc-logo-full { opacity: 0; }
.is-collapsed .cc-logo-mark { opacity: 1; }

.cc-tip {
  translate: 0 -50%;
  box-shadow: var(--shadow-dropdown, 0 4px 12px rgba(0, 0, 0, 0.25));
  animation: cc-tip-in 80ms ease-out;
}
.cc-tip::before {
  content: '';
  position: absolute;
  top: 50%;
  right: 100%;
  translate: 0 -50%;
  border: 5px solid transparent;
  border-right-color: var(--color-cc-text);
}
@keyframes cc-tip-in {
  from { opacity: 0; translate: -4px -50%; }
  to { opacity: 1; translate: 0 -50%; }
}
/* Bottom placement (top bar): centered under the trigger, arrow on top */
.cc-tip.is-bottom { translate: -50% 0; animation-name: cc-tip-in-down; }
.cc-tip.is-bottom::before {
  top: auto; right: auto; bottom: 100%; left: calc(50% + var(--tip-arrow, 0px));
  translate: -50% 0;
  border-right-color: transparent;
  border-bottom-color: var(--color-cc-text);
}
@keyframes cc-tip-in-down {
  from { opacity: 0; translate: -50% -4px; }
  to { opacity: 1; translate: -50% 0; }
}
/* Left placement (right-edge menus): mirror of the rail tip, arrow on the right */
.cc-tip.is-left { translate: -100% -50%; animation-name: cc-tip-in-left; }
.cc-tip.is-left::before {
  right: auto; left: 100%;
  border-right-color: transparent;
  border-left-color: var(--color-cc-text);
}
@keyframes cc-tip-in-left {
  from { opacity: 0; translate: calc(-100% + 4px) -50%; }
  to { opacity: 1; translate: -100% -50%; }
}

/* Tier-locked nav items. Spec hex in light mode; dark mode maps onto theme tokens. */
.cc-locked { --lk-fg: #646973; --lk-fg-hover: #3A3F47; --pro-bg: var(--primary-subtle); --pro-border: color-mix(in srgb, var(--primary) 22%, transparent); --pro-fg: var(--primary); color: var(--lk-fg); }
.cc-locked:hover, .cc-locked:focus-visible { color: var(--lk-fg-hover); }
:global(.dark) .cc-locked { --lk-fg: var(--text-secondary); --lk-fg-hover: var(--text-primary); }
.cc-pro-chip {
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  height: 20px; min-width: 20px; padding: 0 5px; border-radius: 6px;
  background: var(--pro-bg); border: 1px solid var(--pro-border); color: var(--pro-fg);
  transition: padding 150ms ease, opacity 150ms ease 80ms;
}
.cc-pro-text {
  max-width: 0; overflow: hidden; opacity: 0;
  font-size: 11px; font-weight: 600; letter-spacing: 0.02em; line-height: 1;
  transition: max-width 150ms ease, margin-left 150ms ease, opacity 150ms ease;
}
.cc-locked:hover .cc-pro-chip, .cc-locked:focus-visible .cc-pro-chip { padding: 0 7px; }
.cc-locked:hover .cc-pro-text, .cc-locked:focus-visible .cc-pro-text { max-width: 2.5em; margin-left: 4px; opacity: 1; }

.cc-upsell {
  --up-bg: #FFFFFF; --up-border: #E2E4E8; --up-title: #14171B; --up-body: #5A5F68;
  width: 248px; padding: 14px; translate: 0 -50%;
  background: var(--up-bg); border: 1px solid var(--up-border); border-radius: 10px;
  box-shadow: 0 8px 24px rgba(17, 20, 24, 0.10), 0 1px 2px rgba(17, 20, 24, 0.06);
  animation: cc-tip-in 80ms ease-out;
}
:global(.dark) .cc-upsell { --up-bg: var(--bg-elevated); --up-border: var(--border-med); --up-title: var(--text-primary); --up-body: var(--text-secondary); }
/* Arrow: rotated square sharing the popover's fill and border */
.cc-upsell::before {
  content: ''; position: absolute; top: 50%; left: -6px; width: 10px; height: 10px;
  background: var(--up-bg); border-left: 1px solid var(--up-border); border-bottom: 1px solid var(--up-border);
  translate: 0 -50%; rotate: 45deg;
}
/* Invisible bridge over the 18px gap so the pointer can reach the button */
.cc-upsell::after { content: ''; position: absolute; top: 0; bottom: 0; right: 100%; width: 18px; }
.cc-upsell-title { font-size: 13px; font-weight: 600; color: var(--up-title); }
.cc-upsell-body { margin-top: 4px; font-size: 12.5px; line-height: 18px; color: var(--up-body); }
.cc-upsell-btn {
  margin-top: 12px; height: 32px; padding: 0 12px; border-radius: 7px;
  background: var(--primary); color: var(--btn-text); font-size: 12.5px; font-weight: 600;
}
.cc-upsell-btn:hover { background: var(--primary-hover); }

@media (prefers-reduced-motion: reduce) {
  .cc-tip, .cc-upsell { animation: none; }
  .cc-pro-chip, .cc-pro-text { transition: none; }
  .cc-sidebar, .cc-main, .cc-nav, .cc-nav > * + *, .cc-section-head, .cc-nav-item, .cc-nav-label, .cc-rail-only, .cc-logo { transition: none; }
}
</style>
